import styled, { css } from 'styled-components';
import { Radio } from '@lmn/design-system';
import type { CustomComponentProps } from '@/app/common/filters/types/shared/Custom/types.tsx';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import LabelNode from '@/app/common/filters/types/shared/LabelNode.tsx';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import React from 'react';
import type { Localization } from '@/app/components/search/common/Localization';
import type { FilterDataOption } from '@/app/common/filters/core/filtersTypes.ts';
import { focusRing } from '@/app/components/search/styles/mixins.ts';
import { Icon } from '@/app/common/icons/Icon';

const CustomRadio = styled(Radio)<{ isMobile?: boolean; localization: Localization }>`
  width: 100%;
  min-height: 48px;

  [data-testid='radio-indicator'] {
    display: none;
  }

  &:has(input:focus-visible) [data-testid='radio-label'] {
    ${focusRing};
  }

  [data-testid='radio-label'] {
    margin: 0;
    width: 100%;
    ${({ theme }) => theme.kallisto.mixins.font('Body')};
  }

  ${({ isMobile }) =>
    isMobile &&
    css`
      background-color: white;
      padding: 16px;
      border-radius: 0;

      &:after {
        content: '';
        position: absolute;
        display: block;
        height: 1px;
        background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundLayer01};
        bottom: 0;
        left: 16px;
        right: 16px;
      }

      &:first-child {
        border-radius: 8px 8px 0 0;
      }

      &:last-child {
        border-radius: 0 0 8px 8px;

        &:after {
          content: none;
        }
      }
    `}
`;

const LabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

type LabelNodeWithCheckProps = {
  option: FilterDataOption;
  isDisabled: boolean;
  label: string | React.JSX.Element;
};

const LabelNodeWithCheck = ({ option, isDisabled, label }: LabelNodeWithCheckProps) => (
  <LabelRow>
    <LabelNode option={{ ...option, label }} isDisabled={isDisabled} />
    {/* Black: the sort radios tint it with a filter tuned for black. */}
    {option.selected && <Icon category="Semantics" name="icon-check" size={20} color="#000000" alt="" />}
  </LabelRow>
);

const SortRadioComponent = ({ option, onChange, name, index }: CustomComponentProps) => {
  const { localization } = usePageWrapperData();
  const { isMobile } = useCommonClient();
  const isDisabled = !!option?.disabled;

  return (
    <CustomRadio
      localization={localization}
      key={option.value}
      id={`${name}_${index}`}
      label={<LabelNodeWithCheck option={option} isDisabled={isDisabled} label={option.label} />}
      onChange={() => onChange(option.value)}
      checked={option.selected}
      disabled={option.disabled}
      isMobile={isMobile}
    />
  );
};

export default SortRadioComponent;
